import {ArrowNarrowRightIcon} from '@heroicons/react/solid';
import type {ChangeEvent} from 'react';
import {useEffect, useRef, useState} from 'react';

import {Input} from './Input';

type PaginationInputProp = {
  value: number;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  isDisabled: boolean;
  dataCy?: string;
  /**
   * Test id for the "go" button, which commits a typed page number. Separate
   * from `dataCy` (the input): the button is conditionally rendered on focus
   * and is the control whose disabled state encodes the page bounds, so a test
   * that proves an out-of-range page is refused has to reach it directly.
   */
  submitDataCy?: string;
};
export default function PaginationInput({
  value,
  onChange,
  onSubmit,
  isDisabled,
  dataCy,
  submitDataCy,
}: PaginationInputProp) {
  const [showSubmitButton, setShowSubmitButton] = useState<boolean>(false);
  const [inputWidth, setInputWidth] = useState('w-10');
  const inputRef = useRef<HTMLInputElement>(null);
  const hiddenSpanRef = useRef<HTMLSpanElement>(null);

  const onInputFocus = () => setShowSubmitButton(true);
  const handleSubmit = () => {
    onSubmit();
    setShowSubmitButton(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow control keys such as Backspace, Tab, etc.
    if (
      ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(
        event.key,
      )
    ) {
      return;
    }
    // Prevent non-numeric key presses
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    // Get pasted data via clipboard API
    const paste = event.clipboardData.getData('text');
    // Prevent the paste if it contains non-numeric characters
    if (!/^\d+$/.test(paste)) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (hiddenSpanRef.current) {
      setInputWidth(`${hiddenSpanRef.current.offsetWidth + 20}px`);
    }
  }, [value]);

  return (
    <div className="relative text-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center border border-cyan-600 rounded-md focus:outline-none">
      <Input
        ref={inputRef}
        className={`max-w-max h-10 text-base font-medium text-cyan-600 placeholder-gray-600 rounded-md focus:border-none focus:outline-none bg-gray-50 border-0 px-0 dark:border-dark-borderHeavy dark:bg-gray-800 dark:focused:bg-transparent`}
        autoComplete="off"
        onChange={onChange}
        value={value}
        onFocus={onInputFocus}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        style={{width: inputWidth}}
        data-cy={dataCy}
      />
      <span
        ref={hiddenSpanRef}
        className="absolute top-0 left-0 invisible h-0 w-auto whitespace-nowrap overflow-hidden"
      >
        {value}
      </span>
      {showSubmitButton && (
        <button
          className="flex items-center pr-1 rounded-r-lg text-cyan-600 bg-gray-50 dark:bg-transparent hover:text-cyan-700 hover:font-semibold disabled:opacity-75 focus:border-none mr-2 pl-2"
          disabled={isDisabled}
          onClick={handleSubmit}
          data-cy={submitDataCy}
        >
          <span>
            <ArrowNarrowRightIcon className="w-6 h-6 mt-1 pr-2" />
          </span>
          go
        </button>
      )}
    </div>
  );
}
